import {
  DragDropContext,
  Draggable,
  Droppable,
  type DraggableProvidedDragHandleProps,
  type DropResult,
} from '@hello-pangea/dnd';
import { isToday } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { RiCalendarTodoFill, RiDraggable } from 'react-icons/ri';
import { _isNil, _notNil } from '@/littledash.ts';
import type { TaskApiId, TasksExecutionAfterSave, TaskV1 } from '@/model/Task.model.ts';
import { DateRenderFormat, DateUtils } from '@/utils/Date.utils.ts';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal.tsx';
import Select from '@/components/UI/Select';
import { taskTypes } from '@/components/Studies/Tasks/Task.tsx';

interface TasksExecutionAfterSaveOption {
  label: string;
  value: TasksExecutionAfterSave;
}

const tasksExecutionAfterSaveOptions: Array<TasksExecutionAfterSaveOption> = [
  { label: 'Focus search bar', value: 'focus_search_bar' },
  { label: 'Move to next animal', value: 'next_animal' },
];

interface ExecuteTasksModalProps {
  tasks: Array<TaskV1>;
  onExecute: (data: { taskApiIds: Array<TaskApiId>; after_save: TasksExecutionAfterSave }) => void;
  onCancel: () => void;
}

interface TaskCardProps {
  task: TaskV1;
  isDragging: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}

const TaskCard: FC<TaskCardProps> = ({ task, isDragging, dragHandleProps }) => {
  return (
    <div
      data-test-component="TaskCard"
      data-test-element="container"
      data-test-key={task.id}
      className="flex flex-row justify-between items-center bg-near-white pa3 ba"
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <div className="flex flex-column f5 near-black">
        <div className="fw5">{task.title}</div>
        <div className="f6 fw1">
          {taskTypes[task.type]} &middot; Affects {task.target.animal_count ?? 0} animals
        </div>
      </div>
      <div {...dragHandleProps} className="pr2 flex items-center">
        <RiDraggable size={25} className="dark-gray" />
      </div>
    </div>
  );
};

export const ExecuteTasksModal: FC<ExecuteTasksModalProps> = ({ tasks, onExecute, onCancel }) => {
  const [taskList, updateTaskList] = useState(() => tasks);
  const [afterSaveAction, setAfterSaveAction] = useState(() => tasksExecutionAfterSaveOptions[0]);

  const taskExecutionDay = useMemo(() => {
    const minTaskStart = tasks
      .map((t) => new Date(t.duration.start))
      .sort((a, b) => b.getTime() - a.getTime())
      .pop();
    if (_notNil(minTaskStart) && !isToday(minTaskStart)) {
      return DateUtils.renderDateTime(minTaskStart.toISOString(), { format: DateRenderFormat.DateDefaultWithDay });
    }
    return null;
  }, [tasks]);

  const handleDragEnd = useCallback((result: DropResult) => {
    const { source, destination } = result;
    if (_isNil(destination)) {
      return;
    }
    if (source.index === destination.index) {
      return;
    }
    updateTaskList((previous) => {
      const update = [...previous];
      const [movedTask] = update.splice(source.index, 1);
      update.splice(destination.index, 0, movedTask);
      return update;
    });
  }, []);

  return (
    <ModalContainer size="small" testKey="execute-tasks-modal">
      <ModalHeader
        title="Execute tasks"
        subText={
          <span>
            Click and drag the <RiDraggable className="dark-gray" /> icon to specify the order tasks will be completed
          </span>
        }
        subTextClasses="f6 lh-copy mt1 measure-wide"
        closeModal={onCancel}
      />
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="task-list">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`flex flex-column ph3 pt2 pb4 overflow-x-auto overflow-y-hidden bb b${
                snapshot.isDraggingOver ? '--dark-blue bg-washed-blue' : '--moon-gray'
              }`}
            >
              {_notNil(taskExecutionDay) ? (
                <div className="flex flex-row items-center fw5 pv3 ph3 bg-washed-blue ba b--dark-blue near-black">
                  <RiCalendarTodoFill />
                  <span className="ml2">{taskExecutionDay}</span>
                </div>
              ) : null}
              {taskList.map((task, index) => (
                <Draggable key={task.id} draggableId={task.id} index={index}>
                  {(dragProvided, dragSnapshot) => (
                    <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} className="mt2">
                      <TaskCard
                        task={task}
                        isDragging={dragSnapshot.isDragging}
                        dragHandleProps={dragProvided.dragHandleProps}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div className="pv3 ph3 flex bb justify-between items-center" data-testid="workflow-execute-after-save-action">
        <label className="mb0 f6">After save</label>
        <Select<TasksExecutionAfterSaveOption>
          options={tasksExecutionAfterSaveOptions}
          defaultValue={afterSaveAction}
          isMulti={false}
          onChange={(option) =>
            setAfterSaveAction(
              tasksExecutionAfterSaveOptions.find((e) => e.value === option?.value) ?? tasksExecutionAfterSaveOptions[0]
            )
          }
        />
      </div>

      <ModalActions
        className="pa3 mt3"
        submitBtnText="Continue"
        cancelBtnText="Close"
        onSubmit={() => onExecute({ taskApiIds: taskList.map((t) => t.id), after_save: afterSaveAction.value })}
        onCancel={() => onCancel()}
      />
    </ModalContainer>
  );
};
